
import { useState, useEffect } from "react";
import { Badge, Button, Card, ErrorBoundary, Alert, Table, Tabs, Tab } from "@weretradeit/ui-react";

interface DatabaseInfo {
  name: string;
  binding: string;
  type: 'D1' | 'R2' | 'KV';
}

interface QueryResult {
  columns: string[];
  rows: any[][];
  rowCount: number;
  duration: number;
}

const AVAILABLE_DATABASES: DatabaseInfo[] = [
  { name: "Shop API Database", binding: "shop_api_db", type: "D1" },
  { name: "Core Lion (Users & Auth)", binding: "core_lion_db", type: "D1" },
  { name: "InventarOwl Database", binding: "inventarOwl_db", type: "D1" },
  { name: "Main Database", binding: "weretradeInfantrie_db", type: "D1" },
];

const AndminerPageContent = () => {
  const [selectedDb, setSelectedDb] = useState<DatabaseInfo>(AVAILABLE_DATABASES[0]);
  const [query, setQuery] = useState("SELECT name FROM sqlite_master WHERE type='table' ORDER BY name;");
  const [result, setResult] = useState<QueryResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'query' | 'structure'>('structure');

  const executeQuery = async () => {
    if (!query.trim()) {
      setError("Query cannot be empty");
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await fetch('/andminer/api/query', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          database: selectedDb.binding,
          query: query.trim(),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: response.statusText }));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const data = await response.json();
      setResult(data);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const loadStructure = () => {
    setQuery("SELECT name FROM sqlite_master WHERE type='table' ORDER BY name;");
    setActiveTab('structure');
    setTimeout(() => executeQuery(), 100);
  };

  useEffect(() => {
    // Auto-load structure on mount
    loadStructure();
  }, [selectedDb]);

  return (
    <div style={{ padding: "24px", maxWidth: "1400px", margin: "0 auto" }}>
      {/* Header */}
      <div style={{ marginBottom: "24px" }}>
        <h1 style={{ margin: 0, marginBottom: "8px", fontSize: "28px" }}>
          🗄️ Andminer
        </h1>
        <p style={{ margin: 0, color: "var(--text-muted)" }}>
          Database browser and query tool for administrators
        </p>
      </div>

      {/* Database Selector */}
      <Card title="Database Selection">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: "12px" }}>
          {AVAILABLE_DATABASES.map((db) => (
            <Card
              key={db.binding}
              className="component-box"
              style={{
                cursor: 'pointer',
                border: selectedDb.binding === db.binding ? '2px solid var(--accent)' : undefined,
              }}
              onClick={() => setSelectedDb(db)}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <strong>{db.name}</strong>
                <Badge tone={db.type === 'D1' ? 'info' : 'success'}>{db.type}</Badge>
              </div>
              <p style={{ margin: "4px 0 0 0", fontSize: "13px", color: "var(--text-muted)" }}>
                {db.binding}
              </p>
            </Card>
          ))}
        </div>
      </Card>

      {/* Query Interface */}
      <Card title="Query Interface" style={{ marginTop: "24px" }}>
        {/* 3.x: activeKey/onSelect statt activeTab/onTabChange, und der Reiter
            heisst eventKey/title statt id/label. */}
        <Tabs activeKey={activeTab} onSelect={(tab: string) => setActiveTab(tab as 'query' | 'structure')}>
          <Tab eventKey="structure" title="Database Structure">
            <div style={{ marginTop: "16px" }}>
              <p style={{ marginTop: 0 }}>View tables, schemas, and database structure.</p>
              <Button onClick={loadStructure} disabled={loading}>
                {loading ? 'Loading...' : 'Refresh Structure'}
              </Button>
            </div>
          </Tab>
          <Tab eventKey="query" title="Custom Query">
            <div style={{ marginTop: "16px" }}>
              <label style={{ display: "block", marginBottom: "8px", fontWeight: 500 }}>
                SQL Query
              </label>
              <textarea
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Enter SQL query..."
                style={{
                  width: "100%",
                  minHeight: "120px",
                  padding: "12px",
                  fontFamily: "monospace",
                  fontSize: "14px",
                  border: "1px solid var(--border)",
                  borderRadius: "var(--radius-md)",
                  resize: "vertical",
                }}
              />
              <div style={{ marginTop: "12px", display: "flex", gap: "8px" }}>
                <Button onClick={executeQuery} disabled={loading}>
                  {loading ? 'Executing...' : 'Execute Query'}
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => setQuery("SELECT name FROM sqlite_master WHERE type='table' ORDER BY name;")}
                >
                  Show Tables
                </Button>
              </div>
            </div>
          </Tab>
        </Tabs>
      </Card>

      {/* Results */}
      {error && (
        <div style={{ marginTop: "24px" }}>
          <Alert variant="error">{error}</Alert>
        </div>
      )}

      {result && (
        <Card title="Query Results" style={{ marginTop: "24px" }}>
          <div style={{ marginBottom: "12px", display: "flex", gap: "8px", alignItems: "center" }}>
            <Badge tone="info">{result.rowCount} rows</Badge>
            <Badge tone="success">{result.duration.toFixed(2)}ms</Badge>
          </div>

          {result.rowCount === 0 ? (
            <p>No rows returned.</p>
          ) : (
            <div style={{ overflowX: "auto" }}>
              {/* 3.x kennt kein `headers`: eine Spalte traegt ihre
                  Ueberschrift selbst und weiss, wie sie eine Zeile malt. Die
                  Zeilen bleiben roh — vorher wurden sie vorab in Elemente
                  umgeschrieben, was das Sortieren unmoeglich machte. */}
              <Table
                columns={result.columns.map((name, i) => ({
                  key: name,
                  header: name,
                  render: (row: unknown[]) =>
                    row[i] === null
                      ? <em style={{ color: 'var(--text-muted)' }}>NULL</em>
                      : String(row[i]),
                }))}
                rows={result.rows}
                rowKey={(_row, i) => i}
              />
            </div>
          )}
        </Card>
      )}

      {/* Quick Queries */}
      <Card title="Quick Queries" style={{ marginTop: "24px" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "8px" }}>
          <Button
            variant="secondary"
            onClick={() => {
              setQuery("SELECT name FROM sqlite_master WHERE type='table' ORDER BY name;");
              setActiveTab('query');
            }}
          >
            List Tables
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              setQuery("SELECT sql FROM sqlite_master WHERE type='table' AND name = 'your_table_name';");
              setActiveTab('query');
            }}
          >
            Show Table Schema
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              setQuery("SELECT COUNT(*) as count FROM your_table_name;");
              setActiveTab('query');
            }}
          >
            Count Rows
          </Button>
        </div>
      </Card>

      {/* Safety Notice */}
      {/* 3.x nimmt den Text als Kind, nicht als `message`, und die Farbe
          heisst `variant` (die v2-Schreibweise "warning" bleibt zulaessig). */}
      <Alert variant="warning" style={{ marginTop: "24px" }}>
        Admin Tool: Query execution is unrestricted. Be careful with UPDATE, DELETE, and DROP statements.
      </Alert>
    </div>
  );
};

export const AndminerPage = () => (
  /* `fallback` ist in 3.x eine FUNKTION (error, reset) => ReactNode. Als
     fertiges Element uebergeben rief React es nie auf — im Fehlerfall waere
     gar nichts erschienen. */
  <ErrorBoundary
    fallback={(_error, reset) => (
      <Card title="Error Loading Andminer">
        <Alert variant="error">Failed to load database browser.</Alert>
        <Button variant="secondary" onClick={reset} style={{ marginTop: 12 }}>Erneut versuchen</Button>
      </Card>
    )}
  >
    <AndminerPageContent />
  </ErrorBoundary>
);
